import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Faqs.css';

const FAQ_SECTIONS = [
	{
		id: 'general',
		title: 'General',
		blurb: 'Qué es PalletCloud y cómo empezar',
		items: [
			{
				q: '¿Qué es PalletCloud?',
				a: 'PalletCloud es una plataforma digital para rentar y publicar espacios de almacén en México. Los clientes encuentran y reservan posiciones de pallet; los anfitriones publican su capacidad y gestionan solicitudes, pagos e In/Out desde un mismo lugar.',
			},
			{
				q: '¿Puedo ser cliente y anfitrión con la misma cuenta?',
				a: 'Sí. Con una sola cuenta puedes alternar entre Modo Cliente (buscar y reservar) y Modo Anfitrión (publicar y gestionar). El cambio se hace desde el menú superior: “Modo Anfitrión” o “Modo Cliente”.',
			},
			{
				q: '¿Cómo creo mi cuenta?',
				a: 'Entra a Iniciar sesión y regístrate con correo o con tu proveedor social (si está habilitado). Después completa tu perfil y, si vas a facturar o reservar, tu información fiscal (RFC) y documentos cuando se soliciten.',
			},
			{
				q: '¿Cómo contacto a soporte?',
				a: 'Usa el botón “¿Tienes dudas?” o el centro de ayuda (?) en la barra superior. También puedes escribir desde Mensajes. Horario de atención habitual: lunes a viernes 9:00–18:00 y sábados 9:00–14:00.',
			},
		],
	},
	{
		id: 'cliente',
		title: 'Para clientes',
		blurb: 'Buscar, reservar, pagar y operar tu mercancía',
		items: [
			{
				q: '¿Cómo busco un almacén?',
				a: 'En el inicio indica lugar, fechas y espacios. Puedes filtrar por tipo de almacenaje, servicios, vehículos, reserva inmediata y más. Guarda favoritos desde el ícono de corazón o en Mis favoritos.',
			},
			{
				q: '¿Qué diferencia hay entre Reserva Inmediata y Consultar Host?',
				a: 'Con Reserva Inmediata, al acreditar el pago la reservación se confirma automáticamente. Con Consultar Host envías una solicitud: el anfitrión debe aprobarla (o liberar espacios si aplica) antes de completar el pago.',
			},
			{
				q: '¿Cómo se calcula el precio que pago?',
				a: 'El total incluye el monto de la renta según el periodo (día, semana, mes o año), más IVA (16%). Si pagas con tarjeta se agrega una comisión de procesamiento (3.6%). Con transferencia SPEI no se cobra esa comisión de tarjeta.',
			},
			{
				q: '¿Qué métodos de pago aceptan?',
				a: 'Tarjeta (Stripe) y transferencia SPEI. En transferencia verás CLABE, beneficiario (PalletCloud) y referencia. Conserva el comprobante hasta que se confirme el pago.',
			},
			{
				q: '¿Dónde veo mis reservaciones?',
				a: 'En Reservaciones (modo cliente) puedes abrir el detalle, ver el estatus, cancelar si aplica, calificar al finalizar y gestionar movimientos In/Out.',
			},
			{
				q: '¿Qué son los movimientos In y Out?',
				a: 'In es el ingreso de mercancía al almacén y Out el retiro. Desde la reservación puedes solicitar In/Out con fecha, hora y productos. Puede haber costo de maniobra (Precio In / Precio Out) más IVA, según la publicación.',
			},
			{
				q: '¿Qué pasa si el host propone otro horario para mi In/Out?',
				a: 'Tu solicitud puede pasar a “Esperando aprobación del cliente”. Revisa la propuesta en la reservación y acepta o responde desde ahí / Mensajes.',
			},
			{
				q: '¿Cuál es la política de cancelación?',
				a: 'Depende de cada publicación: reembolso total (100%), parcial (50%) o sin reembolso (0%), normalmente si cancelas hasta 7 días antes del inicio. En la ficha del almacén verás “Cancelación gratuita” o la política correspondiente.',
			},
			{
				q: '¿Puedo calificar un almacén?',
				a: 'Sí, al completar una reservación puedes dejar estrellas y comentario. Las calificaciones se muestran en la ficha del almacén para otros clientes.',
			},
			{
				q: '¿Qué es Mi Stock?',
				a: 'Es el inventario de tu mercancía consignada en almacenes PalletCloud (cuando aplica integración). Desde ahí das seguimiento a lo que tienes almacenado.',
			},
		],
	},
	{
		id: 'anfitrion',
		title: 'Para anfitriones',
		blurb: 'Publicar, precios, solicitudes y operación',
		items: [
			{
				q: '¿Cómo me convierto en anfitrión?',
				a: 'Ve a “Renta tu almacén” / Unirte, acepta los términos de anfitrión y entra a Modo Anfitrión. Después da de alta Persona (física o moral), Almacén y Publicación desde Gestión.',
			},
			{
				q: '¿Qué necesito para publicar un almacén?',
				a: 'Una Persona ligada (con documentos aprobados), un Almacén completo (domicilio, horarios, servicios, seguros, documentos) y una Publicación con precios y política de cancelación. Para publicar se requieren al menos 6 fotografías y que los documentos requeridos estén aprobados.',
			},
			{
				q: '¿Qué documentos debo subir?',
				a: 'Según Persona Física o Moral: comprobante de domicilio, constancia de situación fiscal, identificación oficial, estado de cuenta y los del almacén que apliquen. Se revisan en estatus Pendiente / En revisión / Aprobado / Rechazado. Peso máximo habitual: 25 MB (PDF).',
			},
			{
				q: '¿Qué es Precio In y Precio Out?',
				a: 'Son cargos de maniobra por recibir (In) o entregar (Out) mercancía, independientes del periodo de renta. Se configuran en la pestaña Precios de la publicación.',
			},
			{
				q: '¿Cómo se calcula lo que recibo?',
				a: 'En el desglose de precios verás el monto, la comisión PalletCloud (5%), el subtotal y los impuestos (IVA 16%). El total estimado es una guía de lo que generarías por periodo.',
			},
			{
				q: '¿Cuándo recibo el pago de una reservación?',
				a: 'Si el cliente no cancela: normalmente un anticipo del 20% siete días antes del inicio y el resto en la fecha de inicio (según las reglas vigentes de la plataforma). Si hay cancelación con política parcial, puedes recibir el porcentaje correspondiente en plazos definidos.',
			},
			{
				q: '¿Cómo apruebo o rechazo una solicitud?',
				a: 'En Reservaciones → Solicitudes (o el detalle de la reservación) verás solicitudes en espera. Puedes Aprobar o Rechazar. Si el estatus es WaitingSpaces, confirma si puedes liberar los espacios pedidos.',
			},
			{
				q: '¿Cómo gestiono In/Out como host?',
				a: 'En Solicitudes de In/Out revisas cada movimiento: aprueba, rechaza o propone otro horario. El cliente verá el cambio y deberá confirmarlo si aplica.',
			},
			{
				q: '¿Qué políticas de cancelación puedo ofrecer?',
				a: 'Reembolso total (100%), parcial (50%) o sin reembolso (0%) para cancelaciones del cliente hasta 7 días antes del inicio. Elige la que mejor se ajuste a tu operación en la publicación.',
			},
			{
				q: '¿Espacios Inmediatos vs Consultar Host?',
				a: 'Puedes separar capacidad: espacios de confirmación inmediata (pago acreditado = reservado) y espacios que requieren tu aprobación previa. Así controlas ocupación y riesgo operativo.',
			},
		],
	},
	{
		id: 'pagos',
		title: 'Pagos y facturación',
		blurb: 'Comisiones, IVA y métodos de pago',
		items: [
			{
				q: '¿PalletCloud retiene comisión?',
				a: 'Sí. En el desglose del anfitrión se muestra una comisión de plataforma del 5% sobre el monto, más el tratamiento de impuestos (IVA 16%) según el flujo de liquidación.',
			},
			{
				q: '¿Por qué mi total con tarjeta es mayor?',
				a: 'Al pagar con tarjeta se agrega aproximadamente 3.6% de comisión del procesador. Con SPEI ese cargo no aplica.',
			},
			{
				q: '¿Puedo facturar?',
				a: 'Sí. Como cliente elige el perfil fiscal (RFC) al reservar. Como anfitrión mantén tu constancia fiscal y datos de la Persona al día para la facturación de tus ingresos.',
			},
			{
				q: '¿Qué periodos de renta existen?',
				a: 'Día, semana, mes y año, según lo que active el anfitrión. Cada periodo puede tener precio, mínimo, máximo y condiciones de crédito/facturación (semanal, mensual o anual).',
			},
		],
	},
];

const FaqItem = ({ item, open, onToggle }) => (
	<div className={'faqItem' + (open ? ' is-open' : '')}>
		<button type="button" className="faqItem__q" onClick={onToggle} aria-expanded={open}>
			<span>{item.q}</span>
			<span className="faqItem__chev" aria-hidden="true">
				<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
					<path d="M6 9l6 6 6-6"/>
				</svg>
			</span>
		</button>
		{open && (
			<div className="faqItem__a">
				<p>{item.a}</p>
			</div>
		)}
	</div>
);

const Faqs = () => {
	const location = useLocation();
	const [query, setQuery] = useState('');
	const [activeSection, setActiveSection] = useState('general');
	const [openKey, setOpenKey] = useState('');

	useEffect(() => {
		const hash = (location.hash || '').replace('#', '');
		if(hash && FAQ_SECTIONS.some((s)=> s.id === hash)){
			setActiveSection(hash);
			const el = document.getElementById(`faq-sec-${hash}`);
			if(el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}, [location.hash]);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if(!q) return FAQ_SECTIONS;
		return FAQ_SECTIONS.map((section) => ({
			...section,
			items: section.items.filter((item) =>
				item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q)
			),
		})).filter((section) => section.items.length > 0);
	}, [query]);

	return (
		<div className="faqsPage">
			<section className="faqsHero">
				<p className="faqsHero__kicker">Centro de ayuda</p>
				<h1>Preguntas frecuentes</h1>
				<p className="faqsHero__lead">
					Respuestas claras para clientes y anfitriones sobre reservas, pagos, publicaciones e In/Out.
				</p>
				<label className="faqsSearch">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18" aria-hidden="true">
						<circle cx="11" cy="11" r="7"/>
						<path d="M20 20l-3.5-3.5"/>
					</svg>
					<input
						type="search"
						value={query}
						onChange={(e)=> setQuery(e.target.value)}
						placeholder="Buscar en las FAQs…"
						aria-label="Buscar en FAQs"
					/>
				</label>
			</section>

			<nav className="faqsTabs" aria-label="Categorías de FAQ">
				{FAQ_SECTIONS.map((section) => (
					<button
						key={section.id}
						type="button"
						className={activeSection === section.id ? 'is-active' : ''}
						onClick={() => {
							setActiveSection(section.id);
							setQuery('');
							const el = document.getElementById(`faq-sec-${section.id}`);
							if(el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
						}}
					>
						{section.title}
					</button>
				))}
			</nav>

			<div className="faqsBody">
				{filtered.length === 0 && (
					<div className="faqsEmpty">
						<p>No encontramos resultados para “{query}”.</p>
						<button type="button" onClick={()=> setQuery('')}>Limpiar búsqueda</button>
					</div>
				)}

				{filtered.map((section) => (
					<section key={section.id} id={`faq-sec-${section.id}`} className="faqsSection">
						<header className="faqsSection__head">
							<h2>{section.title}</h2>
							<p>{section.blurb}</p>
						</header>
						<div className="faqsSection__list">
							{section.items.map((item, idx) => {
								const key = `${section.id}-${idx}`;
								return (
									<FaqItem
										key={key}
										item={item}
										open={openKey === key}
										onToggle={() => setOpenKey(openKey === key ? '' : key)}
									/>
								);
							})}
						</div>
					</section>
				))}
			</div>

			<section className="faqsCta" id="contacto">
				<div>
					<h2>¿No encontraste lo que buscabas?</h2>
					<p>Escríbenos desde Mensajes o usa el botón de ayuda en la esquina. También puedes publicar tu almacén o explorar espacios disponibles.</p>
				</div>
				<div className="faqsCta__actions">
					<Link to="/mensajes" className="faqsCta__primary">Ir a Mensajes</Link>
					<Link to="/unirte" className="faqsCta__secondary">Publicar almacén</Link>
					<Link to="/" className="faqsCta__ghost">Explorar almacenes</Link>
				</div>
			</section>
		</div>
	);
};

export default Faqs;
